<div class="relative">
  @let isInitialDataLoading = !isInitialDataLoaded() && configurations().isLoaderEnabled;
  @let loadingSubsequentData = isInitialDataLoaded() && loadingData();

  <div [class.hidden]="isInitialDataLoading">
    <div class="flex items-center gap-2">
      @if (configurations().editScope !== ECrudScope.none) {
        @let isEditDisabled =
          (selectedItems()!.length !== 1 && configurations().editScope === ECrudScope.single) ||
          (!selectedItems()!.length && configurations().editScope === ECrudScope.bulk) ||
          maxEditScope()! === ECrudScope.none ||
          (selectedItems()!.length >= 2 && maxEditScope() < ECrudScope.bulk);

        <button mat-mini-fab color="basic" class="me-2 v-middle" [disabled]="isEditDisabled" (click)="editClick.emit()">
          <app-icon
            src="/icons/edit.svg"
            alt="Edit"
            [size]="18"
            [color]="isEditDisabled ? 'var(--shikenso-gray-400)' : 'var(--shikenso-purple-base)'"
          />
        </button>
      }

      @if (configurations().deleteScope !== ECrudScope.none) {
        @let isDeleteDisabled =
          (selectedItems()!.length !== 1 && configurations().deleteScope === ECrudScope.single) ||
          (!selectedItems()!.length && configurations().deleteScope === ECrudScope.bulk) ||
          maxDeleteScope()! === ECrudScope.none ||
          (selectedItems()!.length >= 2 && maxDeleteScope() < ECrudScope.bulk);

        <button mat-mini-fab color="basic" class="v-middle" [disabled]="isDeleteDisabled" (click)="deleteClick.emit()">
          <app-icon
            src="/icons/trash.svg"
            alt="Delete"
            [size]="18"
            [color]="isDeleteDisabled ? 'var(--shikenso-gray-400)' : 'var(--shikenso-purple-base)'"
          />
        </button>
      }

      <div class="flex items-center gap-5 w-full">
        @if (configurations().isSearchEnabled) {
          <mat-form-field class="custom-form w-full mb-6">
            <app-icon src="/icons/search.svg" matPrefix alt="Search" [size]="16" />
            <input matInput [formControl]="search" [placeholder]="'shared.search' | translate" />
          </mat-form-field>
        }

        @if (configurations().filters.length) {
          @for (filter of configurations().filters; track filter.key) {
            <div class="mb-6 flex items-center">
              @if (filter.customFilterTemplate) {
                <ng-container [ngTemplateOutlet]="filter.customFilterTemplate" />
              } @else {
                <mat-form-field class="custom-form !min-w-[220px]">
                  <mat-label>{{ filter.label }}</mat-label>
                  <mat-select
                    [placeholder]="filter.placeholder ?? ''"
                    [multiple]="filter.multiple"
                    (selectionChange)="onFilterChange(filter.key, $event.value)"
                  >
                    @for (opt of filter.options; track opt.value) {
                      <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              }
            </div>
          }
        }
      </div>
    </div>

    <div class="text-right">
      @if (configurations().downloadScope.excelTemplate) {
        <button color="basic" mat-flat-button class="me-2">
          <app-icon src="/icons/download.svg" alt="Download" [size]="18" />
          {{ 'button.downloadExcelTemplate' | translate }}
        </button>
      }

      @if (configurations().downloadScope.excelWithData) {
        <button color="basic" mat-flat-button class="me-2">
          <app-icon src="/icons/file-description.svg" alt="Description" [size]="18" />
          {{ 'button.downloadExcelWithData' | translate }}
        </button>
      }
    </div>
  </div>

  <div
    class="table-container simple-hover-table"
    [class.non-clickable-table]="!configurations().isRowClickEnabled"
    [class.hidden]="isInitialDataLoading"
    [style.margin]="configurations().tableMargin"
    [style.padding]="configurations().tablePadding"
    [style.--datatable-row-color]="configurations().rowColor ?? null"
  >
    <table
      #table
      mat-table
      matSort
      cdkDropList
      cdkDropListData="dataSource"
      cdkDropListLockAxis="y"
      cdkDropListOrientation="vertical"
      class="datatable-with-draggable-rows"
      [dataSource]="configurations().isClientSide ? dataSource : formattedRowData()"
      [trackBy]="trackBy()"
      (cdkDropListDropped)="drop($event)"
      (cdkDropListSorted)="dragSorted($event)"
      (matSortChange)="sort.next($event)"
    >
      <ng-container matColumnDef="drag">
        <th mat-header-cell *matHeaderCellDef>
          <div>
            {{ 'field.order' | translate }}
          </div>
        </th>
        <td
          width="50px"
          mat-cell
          *matCellDef="let row; let i = index"
          cdkDrag
          cdkDragHandle
          cdkDragRootElement="tr"
          cdkDragBoundary=".datatable-with-draggable-rows"
          (cdkDragStarted)="currentDragIndex = i"
          [cdkDragData]="row"
        >
          <div>
            <div class="d-flex align-items-center">
              <app-icon src="/icons/grip-vertical.svg" alt="Drag to reorder" [size]="18" />
              @if (!configurations().areCheckboxesEnabled) {
                <div class="ms-3">{{ i + 1 }}</div>
              }
            </div>
            <div class="cdk-drag-preview ml-3" *cdkDragPreview>
              <span class="draggable-row-new-order">
                {{ 'system.label.newOrder' | translate }}: {{ (currentDragIndex || 0) + 1 }}
              </span>
            </div>
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="checkbox">
        <th mat-header-cell *matHeaderCellDef>
          <mat-checkbox
            color="primary"
            [disabled]="!rowData().length"
            [checked]="isAllSelected()"
            [indeterminate]="checkboxModel.hasValue() && !isAllSelected()"
            (change)="$event ? toggleAllRows() : null"
          >
          </mat-checkbox>
        </th>

        <td mat-cell width="50px" *matCellDef="let row; let i = index">
          <mat-checkbox
            color="primary"
            [checked]="isCheckedStatuses().length >= i + 1 && isCheckedStatuses()[i].isChecked"
            [disabled]="configurations().disableCheckboxCondition(row)"
            (click)="$event.stopPropagation()"
            (change)="$event ? checkboxModel.toggle(row.id) : null"
          >
          </mat-checkbox>
        </td>
      </ng-container>

      @for (customColumn of customColumns(); track customColumn.uniqueColumnId) {
        <ng-container [matColumnDef]="customColumn.uniqueColumnId">
          <th
            mat-header-cell
            class="datatable-header"
            *matHeaderCellDef
            mat-sort-header
            [disabled]="!customColumn.isSortable"
            [ngClass]="customColumn.headerClasses"
          >
            @if (customColumn.name !== 'actions') {
              <div class="header-name">{{ 'field.' + customColumn.name | translate }}</div>
            }
          </th>

          <td
            mat-cell
            *matCellDef="let row; let i = index"
            [ngClass]="{
              'mat-cell-text-overflow': customColumn.isIncludedForBigDataStyle,
              'max-width-300': customColumn.isIncludedForBigDataStyle,
            }"
          >
            <ng-container
              *ngTemplateOutlet="
                customColumn.template;
                context: { row: row, handleEvent: customColumn.handleEvent, index: i }
              "
            />
          </td>
        </ng-container>
      }

      @for (simpleColumn of simpleColumns(); track simpleColumn) {
        <ng-container [matColumnDef]="simpleColumn.uniqueColumnId">
          <th
            mat-header-cell
            mat-sort-header
            class="datatable-header"
            *matHeaderCellDef
            [disabled]="!simpleColumn.isSortable"
            [ngClass]="simpleColumn.headerClasses"
          >
            <div class="header-name">{{ simpleColumn.name ?? 'field.' + simpleColumn.id | translate }}</div>
          </th>
          <td
            mat-cell
            matRipple
            class="max-width-300"
            *matCellDef="let row"
            [ngClass]="{ 'mat-cell-text-overflow': !simpleColumn.isExcludedForBigDataStyle }"
          >
            <div>
              {{ row[simpleColumn.uniqueColumnId] }}
            </div>
          </td>
        </ng-container>
      }

      @if (configurations().isHeaderRowEnabled) {
        <tr mat-header-row *matHeaderRowDef="columnNames()" class="header-row"></tr>
      }

      <tr
        mat-row
        class="data-row"
        *matRowDef="let row; columns: columnNames(); let i = index"
        [attr.data-testid]="'datatable-row-' + i"
        [class.hidden]="loadingSubsequentData"
        [class.!cursor-default]="!configurations().isRowClickEnabled"
        (click)="configurations().isRowClickEnabled && rowClick.emit({ event: $event, row })"
      ></tr>

      <tr class="mat-row" *matNoDataRow>
        @if ((!loadingData() || !configurations().isLoaderEnabled) && this.configurations().showNoDataText) {
          <td class="mat-cell ps-3">
            {{ 'shared.noResults' | translate }}
          </td>
        }

        @if ((!loadingData() || !configurations().isLoaderEnabled) && !this.configurations().showNoDataText) {
          <td class="mat-cell ps-3">
            <br />
            <br />
          </td>
        }
      </tr>
    </table>
  </div>

  @if (configurations().isLoaderEnabled && (!isInitialDataLoaded() || loadingData()) && loaderConfig()) {
    @let showHeaderControls = loaderConfig()!.showHeaderControls ?? true;
    @let showColumns = loaderConfig()!.showColumns ?? true;

    <div [class.-mt-7]="loadingSubsequentData">
      <app-skeleton-loader
        [containerClass]="loaderConfig()?.containerClass ?? 'skeleton-container'"
        [showHeaderControls]="showHeaderControls"
        [showCreateButton]="loaderConfig()!.showCreateButton ?? false"
        [showFilters]="loadingSubsequentData ? false : (loaderConfig()!.showFilters ?? false)"
        [filters]="loaderConfig()!.filters ?? []"
        [filterContainerClass]="loaderConfig()!.filterContainerClass"
        [showSearchField]="loadingSubsequentData ? false : (loaderConfig()!.showSearchField ?? true)"
        [showColumns]="loadingSubsequentData ? false : showColumns"
        [showPaginator]="loadingSubsequentData ? false : (loaderConfig()!.showPaginator ?? true)"
        [headerMargin]="loaderConfig()!.headerMargin ?? '0'"
        [columns]="loaderConfig()!.columns ?? []"
        [rows]="
          !configurations().isClientSide && loadingSubsequentData
            ? paginator.value.pageSize
            : (loaderConfig()!.rows ?? 5)
        "
      />
    </div>
  }

  @if (configurations().isPaginationEnabled) {
    <mat-paginator
      class="custom-paginator"
      [class.!hidden]="isInitialDataLoading"
      [pageSizeOptions]="configurations().pageSizeOptions"
      [length]="total()"
      [pageIndex]="paginatorState().pageIndex"
      [pageSize]="paginatorState().pageSize"
      (page)="paginator.next($event)"
    />
  }
</div>
